import { cn } from '../lib/cn'

/**
 * A quiet circle of progress: a track, a faint arc for what is committed,
 * and the solid arc for what is done, both rising from twelve o'clock. One
 * mark for "how far along" that asks for no numbers — the numbers live
 * beside it when they matter.
 *
 * Fractions clamp to the whole ring. An arc at zero is not drawn at all, so
 * an untouched ring stays a clean circle rather than sprouting a dot.
 */
export function ProgressRing({
  value,
  secondary,
  size = 26,
  label,
  className,
}: {
  /** The solid arc: what is done, as a fraction of the whole. */
  value: number
  /** The faint arc beneath it: what is committed but not yet done. */
  secondary?: number
  size?: number
  label?: string
  className?: string
}) {
  const clamp = (fraction: number) => Math.max(0, Math.min(1, fraction))
  const done = clamp(value)
  const committed = secondary === undefined ? 0 : clamp(secondary)
  const stroke = 2.5
  const radius = (32 - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const arc = (fraction: number, faint?: boolean) =>
    fraction <= 0 ? null : (
      <circle
        cx={16}
        cy={16}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${circumference * fraction} ${circumference}`}
        className={faint ? 'stroke-foreground/25' : 'stroke-foreground'}
      />
    )

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role={label ? 'img' : undefined}
      aria-label={label}
      className={cn('shrink-0 -rotate-90', className)}
    >
      <circle cx={16} cy={16} r={radius} fill="none" strokeWidth={stroke} className="stroke-tint" />
      {committed > done ? arc(committed, true) : null}
      {arc(done)}
    </svg>
  )
}
